import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { hydrateSelectedItemsAction } from '@air/redux-selected-items';

import { mixedItemToSelectable } from '~/components/TableView/utils';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { centralizedBoardIdSelector } from '~/store/centralizedBoard/selectors';
import { setSelectableGalleryItemsAction } from '~/store/selectedItems/actions';
import { type SelectableGalleryItem } from '~/store/selectedItems/types';
import { type UseGalleryMixedItem } from '~/swr-hooks/gallery/types';

export const useTableSelectableItems = (selectableData: UseGalleryMixedItem[], supportsPersistence = false) => {
  const dispatch = useDispatch();
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const includeNestedItems = useViewsSelectAllStore((state) => state.includeNestedItems);
  const selectAllRootBoardId = useViewsSelectAllStore((state) => state.selectAllRootBoardId);
  const currentBoardId = useSelector(centralizedBoardIdSelector);

  useEffect(() => {
    const selectableItems = selectableData.reduce<SelectableGalleryItem[]>((acc, curr) => {
      acc.push(mixedItemToSelectable(curr));
      return acc;
    }, []);

    dispatch(
      setSelectableGalleryItemsAction({
        selectableItems,
        persist: supportsPersistence || (selectAll && includeNestedItems),
      }),
    );

    // When selectAll is true at root board, select only assets and files (not boards)
    // Don't re-hydrate on navigation - selection persists from root board
    const isAtRootBoard = !selectAllRootBoardId || currentBoardId === selectAllRootBoardId;
    if (selectAll && isAtRootBoard) {
      const assetsAndFilesOnly = selectableItems.filter((item) => item.type !== 'board');
      dispatch(hydrateSelectedItemsAction({ selectedItems: assetsAndFilesOnly }));
    }
  }, [
    dispatch,
    selectAll,
    includeNestedItems,
    selectAllRootBoardId,
    currentBoardId,
    selectableData,
    supportsPersistence,
  ]);
};
